/**
 * Root application component — the host shell for every route.
 *
 * Owns the persistent UI chrome (header, footer, cookie banner, AI
 * assistant) that surrounds the router outlet, and wires up a global
 * `gtag` page-view event on every successful navigation so Google
 * Analytics tracks SPA route changes the way it would track
 * full-page loads on a server-rendered site.
 *
 * The component is `OnPush` per CLAUDE.md §4 (Angular standards) and
 * uses `inject()` rather than constructor parameter injection. The
 * `takeUntilDestroyed()` operator on the router subscription means we
 * don't have to track an unsubscribe handle explicitly — the
 * subscription is torn down automatically when the component leaves
 * the tree (which only happens on app teardown, but the discipline
 * matters).
 *
 * @see HeaderComponent          top navigation
 * @see FooterComponent          bottom links + legal
 * @see CookieConsentComponent   GDPR/CCPA banner
 * @see AiAssistantComponent     floating chat widget
 */
import { Component, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { CookieConsentComponent } from './components/cookie-consent/cookie-consent.component';
import { AiAssistantComponent } from './components/ai-assistant/ai-assistant.component';
import { OfflineBannerComponent } from './components/offline-banner/offline-banner.component';
import { CookieConsentService } from './services/cookie-consent.service';
import { CapabilityService, applyCapabilityClasses } from './services/capability.service';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Ambient declaration for the Google Analytics gtag.js script loaded in index.html. */
declare function gtag(command: string, targetId: string, config?: Record<string, any>): void;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    HeaderComponent,
    FooterComponent,
    CookieConsentComponent,
    AiAssistantComponent,
    OfflineBannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  /** Browser tab title; also set imperatively in the constructor. */
  title = 'EntropyGroupLimited';

  private titleService = inject(Title);
  private router = inject(Router);
  private cookies = inject(CookieConsentService);
  private capabilities = inject(CapabilityService);

  constructor() {
    this.titleService.setTitle('EntropyGroupLimited');

    // Mirror device/browser capability onto <html> (save-data,
    // reduced-motion, low-end-device) so the global stylesheet can
    // degrade animations and decorative effects on constrained devices.
    // An effect keeps the classes in sync if the user toggles Data Saver
    // or the OS reduced-motion setting mid-session.
    effect(() => applyCapabilityClasses(this.capabilities));

    // Emit a GA page_view on every successful route change, but only
    // when the user has explicitly accepted cookies. PR #521's docs
    // sweep flagged that this previously fired unconditionally — a
    // real GDPR/CCPA gap. `gtag` itself is also defensively checked
    // because the gtag.js script may be blocked by a content-blocker.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event: NavigationEnd) => {
        if (!this.cookies.hasAccepted()) return;
        if (typeof gtag !== 'function') return;
        gtag('config', 'G-H0Q0SKEFG6', {
          page_path: event.urlAfterRedirects,
        });
      });
  }
}
